import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Wand2, RotateCcw, X, Plus, Palette, Type, Layout, Target, Pencil } from "lucide-react";
import { toast } from "sonner";
import { VisualEditor } from "./VisualEditor";

const sanitizeSvg = (input: string) => {
  let svg = input.trim();
  svg = svg.replace(/<script[\s\S]*?<\/script>/gi, '');
  svg = svg.replace(/<a[\s\S]*?<\/a>/gi, (match) => {
    const content = match.replace(/<\/?a[^>]*>/gi, '');
    return content;
  });
  svg = svg.replace(/\s+href\s*=\s*["'][^"']*["']/gi, '');
  svg = svg.replace(/\s+xlink:href\s*=\s*["'][^"']*["']/gi, '');
  svg = svg.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  svg = svg.replace(/<svg(\s[^>]*)?>/i, (m) => {
    let tag = m;
    if (!/xmlns=/.test(tag)) tag = tag.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
    if (!/xmlns:xlink=/.test(tag)) tag = tag.replace('<svg', '<svg xmlns:xlink="http://www.w3.org/1999/xlink"');
    return tag;
  });
  // Responsive attributes
  svg = svg.replace(/<svg([^>]*)>/i, (m, attrs) => {
    let a = attrs || '';
    a = a.replace(/\swidth="[^"]*"/i, '');
    a = a.replace(/\sheight="[^"]*"/i, '');
    const hasPAR = /preserveAspectRatio=/i.test(a);
    const par = hasPAR ? '' : ' preserveAspectRatio="xMidYMid meet"';
    const styleAttr = /style=/i.test(a) ? '' : ' style="max-width:100%;height:auto;"';
    return `<svg${a} width="100%" height="auto"${par}${styleAttr}>`;
  });
  svg = svg.replace(/&(?!amp;|lt;|gt;|quot;|apos;|#\d+;|#x[0-9A-Fa-f]+;)/g, '&amp;');
  // Embed local Font Awesome to render icons
  const faStyle = `
  <defs>
    <style><![CDATA[
      @font-face { font-family: 'Font Awesome 6 Free'; src: url('/fonts/fa-solid-900.woff2') format('woff2'); font-weight: 900; font-display: swap; }
      @font-face { font-family: 'Font Awesome 6 Brands'; src: url('/fonts/fa-brands-400.woff2') format('woff2'); font-weight: 400; font-display: swap; }
      .fa, .fas, .far, .fab, .fal, .fad, text[class*="fa"], tspan[class*="fa"] { font-family: 'Font Awesome 6 Free','Font Awesome 6 Brands' !important; font-weight: 900; }
    ]]></style>
  </defs>`;
  if (svg.includes('<svg')) {
    svg = svg.replace(/<svg([^>]*)>/i, (m) => `${m}\n${faStyle}`);
  }
  return svg;
};

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: 'visual' | 'text';
  currentContent: string;
  onEdit: (prompt: string, visualTypes?: string[], intensity?: string, options?: { mode: 'describe' | 'convert'; originalSvg?: string; direct?: boolean }) => Promise<void>;
  isGenerating?: boolean;
  previewContent?: string;
  title?: string;
}

const visualTypes = [
  { value: "auto", label: "Auto (AI Decides)", icon: Target, preview: "🤖 AI selects best type" },
  
  // Mind Maps & Concept Maps
  { value: "mindmap", label: "Mind Map", icon: Layout, preview: "🧠 Central idea with branches" },
  { value: "concept-map", label: "Concept Map", icon: Layout, preview: "🔗 Connected concepts with links" },
  { value: "spider-diagram", label: "Spider Diagram", icon: Layout, preview: "🕷️ Central hub with radiating topics" },
  { value: "bubble-map", label: "Bubble Map", icon: Layout, preview: "🫧 Topics in connected bubbles" },
  
  // Flowcharts & Process
  { value: "flowchart", label: "Flowchart", icon: Layout, preview: "➡️ Decision flow with boxes" },
  { value: "process-diagram", label: "Process Diagram", icon: Layout, preview: "⚙️ Step-by-step workflow" },
  { value: "workflow", label: "Workflow", icon: Layout, preview: "🔄 Business process flow" },
  { value: "swimlane", label: "Swimlane Diagram", icon: Layout, preview: "🏊 Process across departments" },
  { value: "cross-functional", label: "Cross-Functional Flow", icon: Layout, preview: "🤝 Multi-team processes" },
  
  // Charts & Graphs
  { value: "pie-chart", label: "Pie Chart", icon: Layout, preview: "🥧 Percentage breakdown" },
  { value: "bar-chart", label: "Bar Chart", icon: Layout, preview: "📊 Comparing quantities" },
  { value: "line-graph", label: "Line Graph", icon: Layout, preview: "📈 Trends over time" },
  { value: "area-chart", label: "Area Chart", icon: Layout, preview: "⛰️ Filled line graphs" },
  { value: "scatter-plot", label: "Scatter Plot", icon: Layout, preview: "🔵 Data point correlation" },
  { value: "bubble-chart", label: "Bubble Chart", icon: Layout, preview: "🫧 3D data visualization" },
  { value: "histogram", label: "Histogram", icon: Layout, preview: "📊 Frequency distribution" },
  { value: "box-plot", label: "Box Plot", icon: Layout, preview: "📦 Statistical distribution" },
  { value: "radar-chart", label: "Radar Chart", icon: Layout, preview: "🎯 Multi-axis comparison" },
  { value: "gauge-chart", label: "Gauge Chart", icon: Layout, preview: "🌡️ Performance meter" },
  { value: "funnel-chart", label: "Funnel Chart", icon: Layout, preview: "🔽 Conversion stages" },
  { value: "waterfall-chart", label: "Waterfall Chart", icon: Layout, preview: "🏔️ Cumulative effects" },
  { value: "heatmap", label: "Heatmap", icon: Layout, preview: "🔥 Intensity visualization" },
  { value: "treemap", label: "Treemap", icon: Layout, preview: "🌳 Hierarchical rectangles" },
  { value: "sunburst", label: "Sunburst Chart", icon: Layout, preview: "☀️ Radial hierarchy" },
  
  // Tables & Grids
  { value: "table", label: "Data Table", icon: Layout, preview: "📋 Rows and columns" },
  { value: "comparison-table", label: "Comparison Table", icon: Layout, preview: "⚖️ Feature comparison" },
  { value: "matrix", label: "Matrix", icon: Layout, preview: "📊 Grid of relationships" },
  { value: "grid-layout", label: "Grid Layout", icon: Layout, preview: "🔲 Organized grid view" },
  { value: "dashboard", label: "Dashboard", icon: Layout, preview: "📊 Multi-widget view" },
  
  // Timelines & Schedules
  { value: "timeline", label: "Timeline", icon: Layout, preview: "📅 Events over time" },
  { value: "gantt-chart", label: "Gantt Chart", icon: Layout, preview: "📊 Project timeline" },
  { value: "roadmap", label: "Roadmap", icon: Layout, preview: "🛣️ Future milestones" },
  { value: "calendar", label: "Calendar", icon: Layout, preview: "📅 Monthly/weekly view" },
  { value: "schedule", label: "Schedule", icon: Layout, preview: "⏰ Time-based agenda" },
  { value: "milestone-chart", label: "Milestone Chart", icon: Layout, preview: "🎯 Key achievements" },
  
  // Organization & Hierarchy
  { value: "org-chart", label: "Organization Chart", icon: Layout, preview: "🏢 Company structure" },
  { value: "hierarchy", label: "Hierarchy Diagram", icon: Layout, preview: "📊 Ranking structure" },
  { value: "family-tree", label: "Family Tree", icon: Layout, preview: "🌳 Genealogy chart" },
  { value: "decision-tree", label: "Decision Tree", icon: Layout, preview: "🌲 Choice pathways" },
  
  // Network & Relationships
  { value: "network-diagram", label: "Network Diagram", icon: Layout, preview: "🕸️ Connected nodes" },
  { value: "graph", label: "Graph Network", icon: Layout, preview: "🔗 Node relationships" },
  { value: "social-network", label: "Social Network", icon: Layout, preview: "👥 People connections" },
  { value: "entity-relationship", label: "ER Diagram", icon: Layout, preview: "🗃️ Database schema" },
  
  // Venn & Set Diagrams
  { value: "venn-diagram", label: "Venn Diagram", icon: Layout, preview: "⭕ Overlapping sets" },
  { value: "euler-diagram", label: "Euler Diagram", icon: Layout, preview: "🔵 Set relationships" },
  { value: "set-diagram", label: "Set Diagram", icon: Layout, preview: "📊 Mathematical sets" },
  
  // Lists & Categories
  { value: "checklist", label: "Checklist", icon: Layout, preview: "✅ Task list" },
  { value: "bullet-list", label: "Bullet List", icon: Layout, preview: "• Simple list items" },
  { value: "numbered-list", label: "Numbered List", icon: Layout, preview: "1. Ordered items" },
  { value: "todo-list", label: "Todo List", icon: Layout, preview: "☐ Task management" },
  { value: "pros-cons", label: "Pros & Cons", icon: Layout, preview: "⚖️ Advantage comparison" },
  
  // Kanban & Boards
  { value: "kanban", label: "Kanban Board", icon: Layout, preview: "📋 Task columns" },
  { value: "scrum-board", label: "Scrum Board", icon: Layout, preview: "🏃 Sprint management" },
  { value: "sticky-notes", label: "Sticky Notes", icon: Layout, preview: "🗒️ Post-it style" },
  { value: "card-layout", label: "Card Layout", icon: Layout, preview: "🃏 Information cards" },
  
  // UI/UX Design
  { value: "wireframe", label: "Wireframe", icon: Layout, preview: "📱 UI skeleton" },
  { value: "mockup", label: "UI Mockup", icon: Layout, preview: "🎨 Design preview" },
  { value: "prototype", label: "Prototype", icon: Layout, preview: "🔧 Interactive design" },
  { value: "user-journey", label: "User Journey", icon: Layout, preview: "🚶 Experience map" },
  { value: "sitemap", label: "Site Map", icon: Layout, preview: "🗺️ Website structure" },
  
  // Infographics & Visual
  { value: "infographic", label: "Infographic", icon: Layout, preview: "📊 Visual information" },
  { value: "poster", label: "Poster Layout", icon: Layout, preview: "🖼️ Promotional design" },
  { value: "brochure", label: "Brochure", icon: Layout, preview: "📄 Tri-fold layout" },
  { value: "flyer", label: "Flyer", icon: Layout, preview: "📄 Single page design" },
  
  // Business & Strategy
  { value: "swot-analysis", label: "SWOT Analysis", icon: Layout, preview: "⚡ Strategic framework" },
  { value: "business-model", label: "Business Model", icon: Layout, preview: "💼 Revenue structure" },
  { value: "value-chain", label: "Value Chain", icon: Layout, preview: "🔗 Business activities" },
  { value: "lean-canvas", label: "Lean Canvas", icon: Layout, preview: "📊 Business plan" },
  
  // Educational & Learning
  { value: "lesson-plan", label: "Lesson Plan", icon: Layout, preview: "📚 Educational structure" },
  { value: "study-guide", label: "Study Guide", icon: Layout, preview: "📖 Learning material" },
  { value: "flashcards", label: "Flashcards", icon: Layout, preview: "🃏 Memory cards" },
  { value: "quiz", label: "Quiz Layout", icon: Layout, preview: "❓ Question format" },
  
  // Technical & System
  { value: "architecture", label: "System Architecture", icon: Layout, preview: "🏗️ Technical structure" },
  { value: "deployment", label: "Deployment Diagram", icon: Layout, preview: "🚀 System deployment" },
  { value: "sequence", label: "Sequence Diagram", icon: Layout, preview: "📋 Interaction flow" },
  { value: "class-diagram", label: "Class Diagram", icon: Layout, preview: "🏛️ Object structure" },
  { value: "use-case", label: "Use Case Diagram", icon: Layout, preview: "👤 System usage" },
];

const quickSuggestions = {
  visual: [
    { label: "Change colors", icon: Palette, prompt: "Update the colors to be more vibrant and modern" },
    { label: "Add icons", icon: Plus, prompt: "Add relevant icons to make it more visual and engaging" },
    { label: "Simplify layout", icon: Layout, prompt: "Simplify the layout and make it cleaner" },
    { label: "More professional", icon: Target, prompt: "Make it look more professional and business-ready" },
  ],
  text: [
    { label: "More casual", icon: Type, prompt: "Make the tone more casual and conversational" },
    { label: "More formal", icon: Type, prompt: "Make the tone more formal and professional" },
    { label: "Shorter", icon: Target, prompt: "Make it shorter and more concise" },
    { label: "More detailed", icon: Plus, prompt: "Add more details and explanations" },
  ]
};

const intensityLevels = [
  { value: "light", label: "Light Changes", description: "Minor adjustments only" },
  { value: "medium", label: "Moderate Changes", description: "Balanced improvements" },
  { value: "strong", label: "Major Changes", description: "Significant redesign" },
];

export const EditDialog = ({ 
  open, 
  onOpenChange, 
  type, 
  currentContent,
  onEdit,
  isGenerating = false,
  previewContent,
  title = "Edit Content"
}: EditDialogProps) => {
  const [editPrompt, setEditPrompt] = useState("");
  const [selectedVisualTypes, setSelectedVisualTypes] = useState<string[]>(["auto"]);
  const [intensity, setIntensity] = useState("medium");
  const [mode, setMode] = useState<'describe' | 'convert'>('describe');
  const [isVisualEditorMode, setIsVisualEditorMode] = useState(false);

  const handleQuickSuggestion = (prompt: string) => {
    setEditPrompt(prev => prev ? `${prev}\n\n${prompt}` : prompt);
  };

  const toggleVisualType = (typeValue: string) => {
    if (typeValue === "auto") {
      setSelectedVisualTypes(["auto"]);
      return;
    }

    setSelectedVisualTypes(prev => {
      const filtered = prev.filter(t => t !== "auto");
      if (filtered.includes(typeValue)) {
        const newTypes = filtered.filter(t => t !== typeValue);
        return newTypes.length === 0 ? ["auto"] : newTypes;
      } else {
        const newTypes = [...filtered, typeValue];
        if (newTypes.length > 4) {
          toast.error("Maximum 4 visual types allowed");
          return prev;
        }
        return newTypes;
      }
    });
  };

  const handleSubmit = async () => {
    if (mode === 'describe' && !editPrompt.trim()) {
      toast.error("Please describe what changes you'd like to make");
      return;
    }

    let enhancedPrompt = '';
    if (type === 'visual') {
      if (mode === 'convert') {
        enhancedPrompt = `Convert the provided SVG to the following visual type(s): ${selectedVisualTypes.join(', ')}.\nCRITICAL: Do NOT change the underlying content, labels, numbers, or relationships. Only transform structure to the target type(s) and keep all text exactly the same unless explicitly requested.${editPrompt.trim() ? `\nUser request: "${editPrompt.trim()}".` : ''}`;
      } else {
        enhancedPrompt = `Edit the provided SVG based on user request: "${editPrompt.trim()}".\nCRITICAL: Preserve ALL content and semantics. Do not change topics, labels, or values. If the request targets a single element (e.g., one icon or a small text), only modify that element. Avoid redesigning the layout unless explicitly requested.`;
      }
    } else {
      enhancedPrompt = `Improve this text based on user feedback: "${editPrompt.trim()}"\n\nOriginal text (for reference): ${currentContent.substring(0, 500)}...`;
    }

    await onEdit(
      enhancedPrompt,
      type === 'visual' && mode === 'convert' ? selectedVisualTypes : undefined,
      intensity,
      { mode, originalSvg: currentContent }
    );
  };

  const resetForm = () => {
    setEditPrompt("");
    setSelectedVisualTypes(["auto"]);
    setIntensity("medium");
    setMode('describe');
    setIsVisualEditorMode(false);
  };

  const handleVisualEditorSave = async (svgContent: string, version?: string) => {
    await onEdit(
      `Visual editor changes applied${version ? ` - ${version}` : ""}`,
      undefined,
      "medium",
      { mode: 'describe', originalSvg: svgContent, direct: true }
    );
    setIsVisualEditorMode(false);
  };

  const handleVisualEditorCancel = () => {
    setIsVisualEditorMode(false);
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      onOpenChange(newOpen);
      if (!newOpen) {
        resetForm();
      }
    }}>
      <DialogContent className={`${isVisualEditorMode ? "max-w-7xl" : "max-w-4xl"} max-h-[90vh] overflow-hidden`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5" />
            {isVisualEditorMode ? "Visual Editor" : title}
          </DialogTitle>
        </DialogHeader>
        
        {isVisualEditorMode && type === 'visual' ? (
          <div className="h-[70vh]">
            <VisualEditor
              svgContent={currentContent}
              onSave={handleVisualEditorSave}
              onCancel={handleVisualEditorCancel}
            />
          </div>
        ) : (
          <>
            <div className="flex gap-6 h-full max-h-[70vh] overflow-hidden">
              {/* Left Panel - Edit Options */}
              <div className="flex-1 space-y-4 overflow-y-auto pr-2">
                {/* Quick Suggestions */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Quick Suggestions</Label>
                  <div className="flex flex-wrap gap-2">
                    {quickSuggestions[type].map((suggestion) => {
                      const Icon = suggestion.icon;
                      return (
                        <Button
                          key={suggestion.label}
                          variant="outline"
                          size="sm"
                          onClick={() => handleQuickSuggestion(suggestion.prompt)}
                          className="text-xs"
                        >
                          <Icon className="w-3 h-3 mr-1" />
                          {suggestion.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <Separator />

                {type === 'visual' && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Edit mode</Label>
                    <div className="flex gap-2">
                      <Button
                        variant={mode === 'describe' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('describe')}
                      >
                        Edit by description
                      </Button>
                      <Button
                        variant={mode === 'convert' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setMode('convert')}
                      >
                        Convert visual type
                      </Button>
                    </div>
                  </div>
                )}

                {/* Custom Prompt */}
                <div>
                  <Label htmlFor="edit-prompt" className="text-sm font-medium">
                    Describe your changes
                  </Label>
                  <Textarea
                    id="edit-prompt"
                    value={editPrompt}
                    onChange={(e) => setEditPrompt(e.target.value)}
                    placeholder={type === 'visual' 
                      ? "Change colors to blue, add more icons, make it simpler..."
                      : "Make it more conversational, add examples, shorter paragraphs..."
                    }
                    className="min-h-[100px] mt-2"
                  />
                </div>

                {/* Visual Type Selection - Convert only */}
                {type === 'visual' && mode === 'convert' && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Convert to visual type (max 4)
                    </Label>
                    <div className="grid grid-cols-2 gap-3 p-4 bg-muted/5 rounded-lg border border-border max-h-96 overflow-y-auto">
                      {visualTypes.map((vType) => {
                        const isSelected = selectedVisualTypes.includes(vType.value);
                        
                        const getPreviewSvg = (type: string) => {
                          switch (type) {
                            case "auto":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded mb-2 flex items-center justify-center">
                                  <Target className="w-6 h-6 text-primary" />
                                </div>
                              );
                            case "mindmap":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="32" cy="24" r="6" fill="#f59e0b" stroke="#d97706" strokeWidth="1"/>
                                    <circle cx="18" cy="14" r="4" fill="#3b82f6" stroke="#2563eb" strokeWidth="1"/>
                                    <circle cx="46" cy="14" r="4" fill="#10b981" stroke="#059669" strokeWidth="1"/>
                                    <circle cx="18" cy="34" r="4" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="1"/>
                                    <circle cx="46" cy="34" r="4" fill="#ef4444" stroke="#dc2626" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="18" y2="14" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="46" y2="14" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="18" y2="34" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="46" y2="34" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "flowchart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="6" width="16" height="10" rx="2" fill="#3b82f6" stroke="#2563eb" strokeWidth="1"/>
                                    <polygon points="32,10 42,16 32,22 22,16" fill="#60a5fa" stroke="#3b82f6" strokeWidth="1"/>
                                    <rect x="48" y="6" width="12" height="10" rx="2" fill="#93c5fd" stroke="#60a5fa" strokeWidth="1"/>
                                    <rect x="8" y="30" width="16" height="10" rx="2" fill="#3b82f6" stroke="#2563eb" strokeWidth="1"/>
                                    <rect x="40" y="30" width="16" height="10" rx="2" fill="#93c5fd" stroke="#60a5fa" strokeWidth="1"/>
                                    <line x1="24" y1="11" x2="22" y2="16" stroke="#4b5563" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <line x1="42" y1="16" x2="48" y2="11" stroke="#4b5563" strokeWidth="2"/>
                                    <line x1="28" y1="22" x2="16" y2="30" stroke="#4b5563" strokeWidth="2"/>
                                    <line x1="36" y1="22" x2="48" y2="30" stroke="#4b5563" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "pie-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="32" cy="24" r="18" fill="#10b981" stroke="#059669" strokeWidth="1"/>
                                    <path d="M32,24 L32,6 A18,18 0 0,1 46.36,14.5 Z" fill="#34d399" stroke="#10b981" strokeWidth="1"/>
                                    <path d="M32,24 L46.36,14.5 A18,18 0 0,1 46.36,33.5 Z" fill="#6ee7b7" stroke="#34d399" strokeWidth="1"/>
                                    <path d="M32,24 L46.36,33.5 A18,18 0 0,1 32,42 Z" fill="#a7f3d0" stroke="#6ee7b7" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "bar-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="20" width="8" height="20" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="1"/>
                                    <rect x="20" y="12" width="8" height="28" fill="#a78bfa" stroke="#8b5cf6" strokeWidth="1"/>
                                    <rect x="32" y="16" width="8" height="24" fill="#c4b5fd" stroke="#a78bfa" strokeWidth="1"/>
                                    <rect x="44" y="8" width="8" height="32" fill="#ddd6fe" stroke="#c4b5fd" strokeWidth="1"/>
                                    <line x1="6" y1="40" x2="56" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "timeline":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <line x1="8" y1="24" x2="56" y2="24" stroke="#06b6d4" strokeWidth="3"/>
                                    <circle cx="16" cy="24" r="4" fill="#0891b2" stroke="#06b6d4" strokeWidth="2"/>
                                    <circle cx="32" cy="24" r="4" fill="#0e7490" stroke="#0891b2" strokeWidth="2"/>
                                    <circle cx="48" cy="24" r="4" fill="#155e75" stroke="#0e7490" strokeWidth="2"/>
                                    <rect x="12" y="8" width="8" height="8" rx="2" fill="#67e8f9" stroke="#22d3ee" strokeWidth="1"/>
                                    <rect x="28" y="32" width="8" height="8" rx="2" fill="#a7f3d0" stroke="#6ee7b7" strokeWidth="1"/>
                                    <rect x="44" y="8" width="8" height="8" rx="2" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "org-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="24" y="4" width="16" height="8" rx="2" fill="#f97316" stroke="#ea580c" strokeWidth="1"/>
                                    <rect x="8" y="18" width="16" height="8" rx="2" fill="#fb923c" stroke="#f97316" strokeWidth="1"/>
                                    <rect x="40" y="18" width="16" height="8" rx="2" fill="#fb923c" stroke="#f97316" strokeWidth="1"/>
                                    <rect x="4" y="32" width="12" height="8" rx="1" fill="#fed7aa" stroke="#fdba74" strokeWidth="1"/>
                                    <rect x="20" y="32" width="12" height="8" rx="1" fill="#fed7aa" stroke="#fdba74" strokeWidth="1"/>
                                    <rect x="36" y="32" width="12" height="8" rx="1" fill="#fed7aa" stroke="#fdba74" strokeWidth="1"/>
                                    <rect x="52" y="32" width="8" height="8" rx="1" fill="#fed7aa" stroke="#fdba74" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="16" y2="18" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="48" y2="18" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="16" y1="26" x2="10" y2="32" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="16" y1="26" x2="26" y2="32" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="48" y1="26" x2="42" y2="32" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="48" y1="26" x2="56" y2="32" stroke="#9ca3af" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "table":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-rose-100 to-rose-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="48" height="32" rx="2" fill="#fecdd3" stroke="#e11d48" strokeWidth="1"/>
                                    <line x1="8" y1="18" x2="56" y2="18" stroke="#e11d48" strokeWidth="2"/>
                                    <line x1="8" y1="28" x2="56" y2="28" stroke="#e11d48" strokeWidth="1"/>
                                    <line x1="24" y1="8" x2="24" y2="40" stroke="#e11d48" strokeWidth="1"/>
                                    <line x1="40" y1="8" x2="40" y2="40" stroke="#e11d48" strokeWidth="1"/>
                                    <rect x="8" y="8" width="48" height="10" fill="#e11d48"/>
                                  </svg>
                                </div>
                              );
                            case "kanban":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="8" width="16" height="32" rx="3" fill="#6366f1" stroke="#4f46e5" strokeWidth="1"/>
                                    <rect x="24" y="8" width="16" height="32" rx="3" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="1"/>
                                    <rect x="44" y="8" width="16" height="32" rx="3" fill="#a855f7" stroke="#9333ea" strokeWidth="1"/>
                                    <rect x="6" y="12" width="12" height="6" rx="2" fill="white"/>
                                    <rect x="6" y="20" width="12" height="6" rx="2" fill="white"/>
                                    <rect x="26" y="12" width="12" height="6" rx="2" fill="white"/>
                                    <rect x="46" y="12" width="12" height="6" rx="2" fill="white"/>
                                    <rect x="46" y="20" width="12" height="6" rx="2" fill="white"/>
                                  </svg>
                                </div>
                              );
                            case "line-graph":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <polyline points="8,35 16,25 24,30 32,20 40,25 48,15 56,18" 
                                             fill="none" stroke="#10b981" strokeWidth="3" strokeLinecap="round"/>
                                    <circle cx="8" cy="35" r="2" fill="#059669"/>
                                    <circle cx="16" cy="25" r="2" fill="#059669"/>
                                    <circle cx="24" cy="30" r="2" fill="#059669"/>
                                    <circle cx="32" cy="20" r="2" fill="#059669"/>
                                    <circle cx="40" cy="25" r="2" fill="#059669"/>
                                    <circle cx="48" cy="15" r="2" fill="#059669"/>
                                    <circle cx="56" cy="18" r="2" fill="#059669"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "venn-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-violet-100 to-violet-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="24" cy="24" r="14" fill="rgba(139, 92, 246, 0.4)" stroke="#8b5cf6" strokeWidth="2"/>
                                    <circle cx="40" cy="24" r="14" fill="rgba(236, 72, 153, 0.4)" stroke="#ec4899" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "concept-map":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="16" height="8" rx="2" fill="#14b8a6" stroke="#0d9488" strokeWidth="1"/>
                                    <rect x="40" y="8" width="16" height="8" rx="2" fill="#5eead4" stroke="#2dd4bf" strokeWidth="1"/>
                                    <rect x="8" y="32" width="16" height="8" rx="2" fill="#5eead4" stroke="#2dd4bf" strokeWidth="1"/>
                                    <rect x="40" y="32" width="16" height="8" rx="2" fill="#14b8a6" stroke="#0d9488" strokeWidth="1"/>
                                    <line x1="24" y1="12" x2="40" y2="12" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="16" y1="16" x2="16" y2="32" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="48" y1="16" x2="48" y2="32" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="24" y1="36" x2="40" y2="36" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "network-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-sky-100 to-sky-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="16" cy="16" r="5" fill="#0ea5e9" stroke="#0284c7" strokeWidth="2"/>
                                    <circle cx="48" cy="16" r="5" fill="#38bdf8" stroke="#0ea5e9" strokeWidth="2"/>
                                    <circle cx="32" cy="32" r="5" fill="#7dd3fc" stroke="#38bdf8" strokeWidth="2"/>
                                    <circle cx="16" cy="32" r="4" fill="#0ea5e9" stroke="#0284c7" strokeWidth="1"/>
                                    <circle cx="48" cy="32" r="4" fill="#38bdf8" stroke="#0ea5e9" strokeWidth="1"/>
                                    <line x1="16" y1="16" x2="48" y2="16" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="16" y1="16" x2="32" y2="32" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="48" y1="16" x2="32" y2="32" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="16" y1="16" x2="16" y2="32" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="48" y1="16" x2="48" y2="32" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "gantt-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="10" width="20" height="4" fill="#64748b" rx="1"/>
                                    <rect x="12" y="18" width="24" height="4" fill="#94a3b8" rx="1"/>
                                    <rect x="16" y="26" width="16" height="4" fill="#cbd5e1" rx="1"/>
                                    <rect x="10" y="34" width="28" height="4" fill="#64748b" rx="1"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#374151" strokeWidth="2"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#374151" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "spider-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="32" cy="24" r="4" fill="#eab308" stroke="#ca8a04" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="48" y2="12" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="52" y2="36" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="16" y2="36" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="12" y2="12" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="32" y2="8" stroke="#6b7280" strokeWidth="2"/>
                                    <circle cx="48" cy="12" r="3" fill="#fbbf24"/>
                                    <circle cx="52" cy="36" r="3" fill="#fbbf24"/>
                                    <circle cx="16" cy="36" r="3" fill="#fbbf24"/>
                                    <circle cx="12" cy="12" r="3" fill="#fbbf24"/>
                                    <circle cx="32" cy="8" r="3" fill="#fbbf24"/>
                                  </svg>
                                </div>
                              );
                            case "bubble-map":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-pink-100 to-pink-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="20" cy="20" r="8" fill="rgba(236, 72, 153, 0.6)" stroke="#ec4899" strokeWidth="1"/>
                                    <circle cx="44" cy="16" r="6" fill="rgba(168, 85, 247, 0.6)" stroke="#a855f7" strokeWidth="1"/>
                                    <circle cx="36" cy="32" r="7" fill="rgba(59, 130, 246, 0.6)" stroke="#3b82f6" strokeWidth="1"/>
                                    <circle cx="16" cy="36" r="5" fill="rgba(16, 185, 129, 0.6)" stroke="#10b981" strokeWidth="1"/>
                                    <line x1="20" y1="20" x2="36" y2="32" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="44" y1="16" x2="36" y2="32" stroke="#9ca3af" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "process-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="20" width="12" height="8" rx="2" fill="#22c55e" stroke="#16a34a" strokeWidth="1"/>
                                    <polygon points="26,24 34,20 34,28" fill="#4ade80" stroke="#22c55e" strokeWidth="1"/>
                                    <rect x="42" y="20" width="12" height="8" rx="2" fill="#86efac" stroke="#4ade80" strokeWidth="1"/>
                                    <line x1="16" y1="24" x2="26" y2="24" stroke="#374151" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <line x1="34" y1="24" x2="42" y2="24" stroke="#374151" strokeWidth="2" markerEnd="url(#arrow)"/>
                                  </svg>
                                </div>
                              );
                            case "workflow":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="16" cy="16" r="6" fill="#6366f1" stroke="#4f46e5" strokeWidth="1"/>
                                    <rect x="28" y="12" width="12" height="8" rx="2" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="1"/>
                                    <circle cx="48" cy="32" r="6" fill="#a855f7" stroke="#9333ea" strokeWidth="1"/>
                                    <line x1="22" y1="16" x2="28" y2="16" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="40" y1="16" x2="44" y2="26" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "swimlane":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="8" width="56" height="32" fill="none" stroke="#3b82f6" strokeWidth="1"/>
                                    <line x1="4" y1="20" x2="60" y2="20" stroke="#3b82f6" strokeWidth="1"/>
                                    <line x1="4" y1="32" x2="60" y2="32" stroke="#3b82f6" strokeWidth="1"/>
                                    <rect x="8" y="12" width="12" height="4" rx="1" fill="#60a5fa"/>
                                    <rect x="28" y="12" width="12" height="4" rx="1" fill="#93c5fd"/>
                                    <rect x="8" y="24" width="12" height="4" rx="1" fill="#60a5fa"/>
                                    <rect x="44" y="24" width="12" height="4" rx="1" fill="#93c5fd"/>
                                    <rect x="24" y="36" width="12" height="4" rx="1" fill="#60a5fa"/>
                                  </svg>
                                </div>
                              );
                            case "cross-functional":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="48" height="32" fill="none" stroke="#8b5cf6" strokeWidth="1"/>
                                    <line x1="24" y1="8" x2="24" y2="40" stroke="#8b5cf6" strokeWidth="1"/>
                                    <line x1="40" y1="8" x2="40" y2="40" stroke="#8b5cf6" strokeWidth="1"/>
                                    <circle cx="16" cy="16" r="3" fill="#a78bfa"/>
                                    <circle cx="32" cy="24" r="3" fill="#c4b5fd"/>
                                    <circle cx="48" cy="32" r="3" fill="#ddd6fe"/>
                                    <line x1="19" y1="16" x2="29" y2="24" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="35" y1="24" x2="45" y2="32" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "area-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <polygon points="8,40 8,30 16,25 24,28 32,20 40,22 48,18 56,20 56,40" 
                                             fill="rgba(20, 184, 166, 0.3)" stroke="#14b8a6" strokeWidth="2"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "scatter-plot":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-red-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="12" cy="30" r="2" fill="#ef4444"/>
                                    <circle cx="18" cy="25" r="2" fill="#f87171"/>
                                    <circle cx="26" cy="32" r="2" fill="#ef4444"/>
                                    <circle cx="34" cy="20" r="2" fill="#fca5a5"/>
                                    <circle cx="42" cy="28" r="2" fill="#ef4444"/>
                                    <circle cx="50" cy="15" r="2" fill="#f87171"/>
                                    <circle cx="24" cy="18" r="2" fill="#fca5a5"/>
                                    <circle cx="38" cy="35" r="2" fill="#ef4444"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "bubble-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="16" cy="30" r="4" fill="rgba(6, 182, 212, 0.6)" stroke="#06b6d4"/>
                                    <circle cx="32" cy="20" r="6" fill="rgba(34, 211, 238, 0.6)" stroke="#22d3ee"/>
                                    <circle cx="48" cy="28" r="5" fill="rgba(103, 232, 249, 0.6)" stroke="#67e8f9"/>
                                    <circle cx="24" cy="35" r="3" fill="rgba(6, 182, 212, 0.6)" stroke="#06b6d4"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "histogram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="25" width="6" height="15" fill="#f59e0b"/>
                                    <rect x="16" y="18" width="6" height="22" fill="#fbbf24"/>
                                    <rect x="24" y="20" width="6" height="20" fill="#f59e0b"/>
                                    <rect x="32" y="15" width="6" height="25" fill="#fbbf24"/>
                                    <rect x="40" y="22" width="6" height="18" fill="#f59e0b"/>
                                    <rect x="48" y="28" width="6" height="12" fill="#fbbf24"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                    <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "box-plot":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-lime-100 to-lime-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="16" y="18" width="32" height="12" fill="none" stroke="#84cc16" strokeWidth="2"/>
                                    <line x1="24" y1="18" x2="24" y2="30" stroke="#84cc16" strokeWidth="2"/>
                                    <line x1="8" y1="24" x2="16" y2="24" stroke="#65a30d" strokeWidth="2"/>
                                    <line x1="48" y1="24" x2="56" y2="24" stroke="#65a30d" strokeWidth="2"/>
                                    <line x1="8" y1="20" x2="8" y2="28" stroke="#65a30d" strokeWidth="2"/>
                                    <line x1="56" y1="20" x2="56" y2="28" stroke="#65a30d" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "radar-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-violet-100 to-violet-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <polygon points="32,12 44,20 40,32 24,32 20,20" 
                                             fill="rgba(139, 92, 246, 0.3)" stroke="#8b5cf6" strokeWidth="2"/>
                                    <line x1="32" y1="24" x2="32" y2="8" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="48" y2="18" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="48" y2="36" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="16" y2="36" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="16" y2="18" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "gauge-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <path d="M 16 32 A 16 16 0 0 1 48 32" fill="none" stroke="#f97316" strokeWidth="4"/>
                                    <path d="M 20 30 A 12 12 0 0 1 44 30" fill="none" stroke="#fb923c" strokeWidth="3"/>
                                    <line x1="32" y1="32" x2="40" y2="20" stroke="#ea580c" strokeWidth="3" strokeLinecap="round"/>
                                    <circle cx="32" cy="32" r="3" fill="#9a3412"/>
                                  </svg>
                                </div>
                              );
                            case "funnel-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <polygon points="8,8 56,8 52,16 12,16" fill="#10b981"/>
                                    <polygon points="12,18 52,18 48,26 16,26" fill="#34d399"/>
                                    <polygon points="16,28 48,28 44,36 20,36" fill="#6ee7b7"/>
                                    <polygon points="20,38 44,38 40,44 24,44" fill="#a7f3d0"/>
                                  </svg>
                                </div>
                              );
                            case "waterfall-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-sky-100 to-sky-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="20" width="8" height="20" fill="#0ea5e9"/>
                                    <rect x="20" y="15" width="8" height="5" fill="#38bdf8"/>
                                    <rect x="32" y="18" width="8" height="3" fill="#7dd3fc"/>
                                    <rect x="44" y="25" width="8" height="15" fill="#0ea5e9"/>
                                    <line x1="16" y1="20" x2="20" y2="20" stroke="#6b7280" strokeWidth="1" strokeDasharray="2,2"/>
                                    <line x1="28" y1="15" x2="32" y2="18" stroke="#6b7280" strokeWidth="1" strokeDasharray="2,2"/>
                                    <line x1="40" y1="18" x2="44" y2="25" stroke="#6b7280" strokeWidth="1" strokeDasharray="2,2"/>
                                    <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "heatmap":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-red-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="8" height="8" fill="#fca5a5"/>
                                    <rect x="16" y="8" width="8" height="8" fill="#f87171"/>
                                    <rect x="24" y="8" width="8" height="8" fill="#ef4444"/>
                                    <rect x="32" y="8" width="8" height="8" fill="#dc2626"/>
                                    <rect x="40" y="8" width="8" height="8" fill="#b91c1c"/>
                                    <rect x="48" y="8" width="8" height="8" fill="#991b1b"/>
                                    <rect x="8" y="16" width="8" height="8" fill="#f87171"/>
                                    <rect x="16" y="16" width="8" height="8" fill="#ef4444"/>
                                    <rect x="24" y="16" width="8" height="8" fill="#dc2626"/>
                                    <rect x="32" y="16" width="8" height="8" fill="#b91c1c"/>
                                    <rect x="40" y="16" width="8" height="8" fill="#991b1b"/>
                                    <rect x="48" y="16" width="8" height="8" fill="#7f1d1d"/>
                                    <rect x="8" y="24" width="8" height="8" fill="#ef4444"/>
                                    <rect x="16" y="24" width="8" height="8" fill="#dc2626"/>
                                    <rect x="24" y="24" width="8" height="8" fill="#b91c1c"/>
                                    <rect x="32" y="24" width="8" height="8" fill="#991b1b"/>
                                    <rect x="40" y="24" width="8" height="8" fill="#7f1d1d"/>
                                    <rect x="48" y="24" width="8" height="8" fill="#450a0a"/>
                                    <rect x="8" y="32" width="8" height="8" fill="#dc2626"/>
                                    <rect x="16" y="32" width="8" height="8" fill="#b91c1c"/>
                                    <rect x="24" y="32" width="8" height="8" fill="#991b1b"/>
                                    <rect x="32" y="32" width="8" height="8" fill="#7f1d1d"/>
                                    <rect x="40" y="32" width="8" height="8" fill="#450a0a"/>
                                    <rect x="48" y="32" width="8" height="8" fill="#1c0000"/>
                                  </svg>
                                </div>
                              );
                            case "treemap":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="28" height="20" fill="#22c55e" stroke="white" strokeWidth="1"/>
                                    <rect x="34" y="4" width="26" height="12" fill="#4ade80" stroke="white" strokeWidth="1"/>
                                    <rect x="34" y="18" width="26" height="6" fill="#86efac" stroke="white" strokeWidth="1"/>
                                    <rect x="4" y="26" width="18" height="18" fill="#bbf7d0" stroke="white" strokeWidth="1"/>
                                    <rect x="24" y="26" width="12" height="18" fill="#dcfce7" stroke="white" strokeWidth="1"/>
                                    <rect x="38" y="26" width="22" height="18" fill="#f0fdf4" stroke="white" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "sunburst":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="32" cy="24" r="6" fill="#eab308"/>
                                    <path d="M32,18 A6,6 0 0,1 38,24 L44,24 A12,12 0 0,0 32,12 Z" fill="#fbbf24"/>
                                    <path d="M38,24 A6,6 0 0,1 32,30 L32,36 A12,12 0 0,0 44,24 Z" fill="#facc15"/>
                                    <path d="M32,30 A6,6 0 0,1 26,24 L20,24 A12,12 0 0,0 32,36 Z" fill="#fde047"/>
                                    <path d="M26,24 A6,6 0 0,1 32,18 L32,12 A12,12 0 0,0 20,24 Z" fill="#fef08a"/>
                                  </svg>
                                </div>
                              );
                            case "comparison-table":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="48" height="32" fill="none" stroke="#3b82f6" strokeWidth="1"/>
                                    <line x1="8" y1="18" x2="56" y2="18" stroke="#3b82f6" strokeWidth="2"/>
                                    <line x1="32" y1="8" x2="32" y2="40" stroke="#3b82f6" strokeWidth="2"/>
                                    <rect x="8" y="8" width="48" height="10" fill="#dbeafe"/>
                                    <circle cx="20" cy="26" r="2" fill="#22c55e"/>
                                    <circle cx="44" cy="26" r="2" fill="#ef4444"/>
                                    <circle cx="20" cy="34" r="2" fill="#ef4444"/>
                                    <circle cx="44" cy="34" r="2" fill="#22c55e"/>
                                  </svg>
                                </div>
                              );
                            case "matrix":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="48" height="32" fill="none" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="24" y1="8" x2="24" y2="40" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="40" y1="8" x2="40" y2="40" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="8" y1="20" x2="56" y2="20" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="8" y1="28" x2="56" y2="28" stroke="#6b7280" strokeWidth="1"/>
                                    <circle cx="16" cy="14" r="2" fill="#ef4444"/>
                                    <circle cx="32" cy="14" r="2" fill="#22c55e"/>
                                    <circle cx="48" cy="14" r="2" fill="#eab308"/>
                                    <circle cx="16" cy="24" r="2" fill="#22c55e"/>
                                    <circle cx="32" cy="24" r="2" fill="#ef4444"/>
                                    <circle cx="48" cy="24" r="2" fill="#22c55e"/>
                                    <circle cx="16" cy="34" r="2" fill="#eab308"/>
                                    <circle cx="32" cy="34" r="2" fill="#22c55e"/>
                                    <circle cx="48" cy="34" r="2" fill="#ef4444"/>
                                  </svg>
                                </div>
                              );
                            case "grid-layout":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="12" height="12" fill="#64748b" rx="1"/>
                                    <rect x="24" y="8" width="12" height="12" fill="#94a3b8" rx="1"/>
                                    <rect x="40" y="8" width="12" height="12" fill="#cbd5e1" rx="1"/>
                                    <rect x="8" y="24" width="12" height="12" fill="#94a3b8" rx="1"/>
                                    <rect x="24" y="24" width="12" height="12" fill="#cbd5e1" rx="1"/>
                                    <rect x="40" y="24" width="12" height="12" fill="#64748b" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "dashboard":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="56" height="40" fill="none" stroke="#6366f1" strokeWidth="1"/>
                                    <rect x="8" y="8" width="24" height="16" fill="#a5b4fc" rx="1"/>
                                    <rect x="36" y="8" width="20" height="8" fill="#c7d2fe" rx="1"/>
                                    <rect x="36" y="20" width="20" height="4" fill="#e0e7ff" rx="1"/>
                                    <rect x="8" y="28" width="12" height="12" fill="#c7d2fe" rx="1"/>
                                    <rect x="24" y="28" width="32" height="12" fill="#a5b4fc" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "roadmap":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <line x1="8" y1="24" x2="56" y2="24" stroke="#10b981" strokeWidth="4"/>
                                    <circle cx="16" cy="24" r="4" fill="#059669"/>
                                    <circle cx="32" cy="24" r="4" fill="#047857"/>
                                    <circle cx="48" cy="24" r="4" fill="#065f46"/>
                                    <rect x="12" y="8" width="8" height="8" fill="#6ee7b7" rx="1"/>
                                    <rect x="28" y="32" width="8" height="8" fill="#a7f3d0" rx="1"/>
                                    <rect x="44" y="8" width="8" height="8" fill="#d1fae5" rx="1"/>
                                    <line x1="16" y1="16" x2="16" y2="20" stroke="#065f46" strokeWidth="2"/>
                                    <line x1="32" y1="28" x2="32" y2="32" stroke="#065f46" strokeWidth="2"/>
                                    <line x1="48" y1="16" x2="48" y2="20" stroke="#065f46" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "calendar":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-pink-100 to-pink-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="12" width="48" height="32" fill="white" stroke="#ec4899" strokeWidth="1"/>
                                    <rect x="8" y="12" width="48" height="8" fill="#f9a8d4"/>
                                    <line x1="8" y1="24" x2="56" y2="24" stroke="#ec4899" strokeWidth="1"/>
                                    <line x1="8" y1="30" x2="56" y2="30" stroke="#ec4899" strokeWidth="1"/>
                                    <line x1="8" y1="36" x2="56" y2="36" stroke="#ec4899" strokeWidth="1"/>
                                    <line x1="20" y1="20" x2="20" y2="44" stroke="#ec4899" strokeWidth="1"/>
                                    <line x1="32" y1="20" x2="32" y2="44" stroke="#ec4899" strokeWidth="1"/>
                                    <line x1="44" y1="20" x2="44" y2="44" stroke="#ec4899" strokeWidth="1"/>
                                    <line x1="18" y1="8" x2="18" y2="16" stroke="#be185d" strokeWidth="2"/>
                                    <line x1="46" y1="8" x2="46" y2="16" stroke="#be185d" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "schedule":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="48" height="32" fill="white" stroke="#eab308" strokeWidth="1"/>
                                    <line x1="8" y1="16" x2="56" y2="16" stroke="#eab308" strokeWidth="1"/>
                                    <rect x="12" y="20" width="16" height="4" fill="#fbbf24" rx="1"/>
                                    <rect x="32" y="20" width="20" height="4" fill="#facc15" rx="1"/>
                                    <rect x="12" y="28" width="24" height="4" fill="#fde047" rx="1"/>
                                    <rect x="40" y="28" width="12" height="4" fill="#fef08a" rx="1"/>
                                    <rect x="12" y="36" width="12" height="4" fill="#fbbf24" rx="1"/>
                                    <line x1="4" y1="12" x2="4" y2="44" stroke="#a16207" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "milestone-chart":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <line x1="8" y1="32" x2="56" y2="32" stroke="#8b5cf6" strokeWidth="3"/>
                                    <polygon points="16,24 20,32 16,40 12,32" fill="#a78bfa"/>
                                    <polygon points="32,20 36,32 32,44 28,32" fill="#c4b5fd"/>
                                    <polygon points="48,16 52,32 48,48 44,32" fill="#ddd6fe"/>
                                    <rect x="12" y="8" width="8" height="6" fill="#e9d5ff" rx="1"/>
                                    <rect x="28" y="4" width="8" height="6" fill="#f3e8ff" rx="1"/>
                                    <rect x="44" y="6" width="8" height="6" fill="#faf5ff" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "hierarchy":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="24" y="4" width="16" height="8" fill="#6b7280" rx="2"/>
                                    <rect x="8" y="20" width="16" height="8" fill="#9ca3af" rx="2"/>
                                    <rect x="40" y="20" width="16" height="8" fill="#9ca3af" rx="2"/>
                                    <rect x="4" y="36" width="12" height="6" fill="#d1d5db" rx="1"/>
                                    <rect x="20" y="36" width="12" height="6" fill="#d1d5db" rx="1"/>
                                    <rect x="36" y="36" width="12" height="6" fill="#d1d5db" rx="1"/>
                                    <rect x="52" y="36" width="8" height="6" fill="#d1d5db" rx="1"/>
                                    <line x1="32" y1="12" x2="16" y2="20" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="48" y2="20" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="16" y1="28" x2="10" y2="36" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="16" y1="28" x2="26" y2="36" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="48" y1="28" x2="42" y2="36" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="48" y1="28" x2="56" y2="36" stroke="#4b5563" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "family-tree":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="32" cy="8" r="4" fill="#22c55e"/>
                                    <circle cx="20" cy="20" r="3" fill="#4ade80"/>
                                    <circle cx="44" cy="20" r="3" fill="#4ade80"/>
                                    <circle cx="12" cy="32" r="3" fill="#86efac"/>
                                    <circle cx="28" cy="32" r="3" fill="#86efac"/>
                                    <circle cx="36" cy="32" r="3" fill="#86efac"/>
                                    <circle cx="52" cy="32" r="3" fill="#86efac"/>
                                    <line x1="32" y1="12" x2="20" y2="17" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="44" y2="17" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="20" y1="23" x2="12" y2="29" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="20" y1="23" x2="28" y2="29" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="44" y1="23" x2="36" y2="29" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="44" y1="23" x2="52" y2="29" stroke="#16a34a" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "decision-tree":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="24" y="4" width="16" height="8" fill="#f59e0b" rx="2"/>
                                    <polygon points="16,16 24,20 16,24 8,20" fill="#fbbf24"/>
                                    <polygon points="48,16 56,20 48,24 40,20" fill="#fbbf24"/>
                                    <rect x="4" y="32" width="12" height="8" fill="#fed7aa" rx="2"/>
                                    <rect x="20" y="32" width="12" height="8" fill="#fed7aa" rx="2"/>
                                    <rect x="36" y="32" width="12" height="8" fill="#fed7aa" rx="2"/>
                                    <rect x="52" y="32" width="8" height="8" fill="#fed7aa" rx="2"/>
                                    <line x1="32" y1="12" x2="16" y2="16" stroke="#d97706" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="48" y2="16" stroke="#d97706" strokeWidth="1"/>
                                    <line x1="16" y1="24" x2="10" y2="32" stroke="#d97706" strokeWidth="1"/>
                                    <line x1="16" y1="24" x2="26" y2="32" stroke="#d97706" strokeWidth="1"/>
                                    <line x1="48" y1="24" x2="42" y2="32" stroke="#d97706" strokeWidth="1"/>
                                    <line x1="48" y1="24" x2="56" y2="32" stroke="#d97706" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "graph":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="16" cy="12" r="4" fill="#06b6d4"/>
                                    <circle cx="48" cy="12" r="4" fill="#22d3ee"/>
                                    <circle cx="16" cy="36" r="4" fill="#67e8f9"/>
                                    <circle cx="48" cy="36" r="4" fill="#a5f3fc"/>
                                    <circle cx="32" cy="24" r="4" fill="#0891b2"/>
                                    <line x1="16" y1="12" x2="48" y2="12" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="16" y1="12" x2="16" y2="36" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="48" y1="12" x2="48" y2="36" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="16" y1="36" x2="48" y2="36" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="16" y1="12" x2="32" y2="24" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="48" y1="12" x2="32" y2="24" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="16" y1="36" x2="32" y2="24" stroke="#0e7490" strokeWidth="2"/>
                                    <line x1="48" y1="36" x2="32" y2="24" stroke="#0e7490" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "social-network":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-rose-100 to-rose-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="32" cy="24" r="5" fill="#e11d48"/>
                                    <circle cx="16" cy="16" r="4" fill="#f43f5e"/>
                                    <circle cx="48" cy="16" r="4" fill="#fb7185"/>
                                    <circle cx="12" cy="32" r="3" fill="#fda4af"/>
                                    <circle cx="52" cy="32" r="3" fill="#fecdd3"/>
                                    <circle cx="32" cy="8" r="3" fill="#fb7185"/>
                                    <circle cx="32" cy="40" r="3" fill="#fda4af"/>
                                    <line x1="32" y1="24" x2="16" y2="16" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="48" y2="16" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="12" y2="32" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="52" y2="32" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="32" y2="8" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="32" y2="40" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="16" y1="16" x2="12" y2="32" stroke="#be185d" strokeWidth="1"/>
                                    <line x1="48" y1="16" x2="52" y2="32" stroke="#be185d" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "entity-relationship":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="20" height="16" fill="#6366f1" stroke="#4f46e5" strokeWidth="1" rx="2"/>
                                    <rect x="36" y="8" width="20" height="16" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="1" rx="2"/>
                                    <polygon points="32,28 40,32 32,36 24,32" fill="#a5b4fc" stroke="#6366f1" strokeWidth="1"/>
                                    <rect x="8" y="32" width="16" height="12" fill="#c7d2fe" stroke="#8b5cf6" strokeWidth="1" rx="2"/>
                                    <rect x="40" y="32" width="16" height="12" fill="#e0e7ff" stroke="#a5b4fc" strokeWidth="1" rx="2"/>
                                    <line x1="28" y1="16" x2="24" y2="32" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="36" y1="16" x2="40" y2="32" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="24" y1="38" x2="24" y2="32" stroke="#4b5563" strokeWidth="1"/>
                                    <line x1="40" y1="38" x2="40" y2="32" stroke="#4b5563" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "euler-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-violet-100 to-violet-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <ellipse cx="24" cy="24" rx="12" ry="8" fill="rgba(139, 92, 246, 0.4)" stroke="#8b5cf6" strokeWidth="2"/>
                                    <ellipse cx="40" cy="24" rx="10" ry="6" fill="rgba(236, 72, 153, 0.4)" stroke="#ec4899" strokeWidth="2"/>
                                    <circle cx="32" cy="16" r="5" fill="rgba(59, 130, 246, 0.4)" stroke="#3b82f6" strokeWidth="2"/>
                                  </svg>
                                </div>
                              );
                            case "set-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="24" height="16" fill="rgba(16, 185, 129, 0.3)" stroke="#10b981" strokeWidth="2" rx="4"/>
                                    <rect x="32" y="24" width="24" height="16" fill="rgba(139, 92, 246, 0.3)" stroke="#8b5cf6" strokeWidth="2" rx="4"/>
                                    <rect x="16" y="16" width="24" height="16" fill="rgba(236, 72, 153, 0.3)" stroke="#ec4899" strokeWidth="2" rx="4"/>
                                  </svg>
                                </div>
                              );
                            case "checklist":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="4" height="4" fill="none" stroke="#22c55e" strokeWidth="1"/>
                                    <polyline points="9,10 10,11 12,9" fill="none" stroke="#22c55e" strokeWidth="1"/>
                                    <line x1="16" y1="10" x2="48" y2="10" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <rect x="8" y="18" width="4" height="4" fill="none" stroke="#22c55e" strokeWidth="1"/>
                                    <polyline points="9,20 10,21 12,19" fill="none" stroke="#22c55e" strokeWidth="1"/>
                                    <line x1="16" y1="20" x2="48" y2="20" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <rect x="8" y="28" width="4" height="4" fill="none" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="16" y1="30" x2="48" y2="30" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <rect x="8" y="38" width="4" height="4" fill="none" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="16" y1="40" x2="48" y2="40" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "bullet-list":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="10" cy="12" r="2" fill="#6b7280"/>
                                    <line x1="16" y1="12" x2="48" y2="12" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <circle cx="10" cy="20" r="2" fill="#6b7280"/>
                                    <line x1="16" y1="20" x2="48" y2="20" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <circle cx="10" cy="28" r="2" fill="#6b7280"/>
                                    <line x1="16" y1="28" x2="48" y2="28" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <circle cx="10" cy="36" r="2" fill="#6b7280"/>
                                    <line x1="16" y1="36" x2="48" y2="36" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "numbered-list":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <text x="10" y="14" fontSize="6" fill="#3b82f6" textAnchor="middle">1</text>
                                    <line x1="16" y1="12" x2="48" y2="12" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <text x="10" y="22" fontSize="6" fill="#3b82f6" textAnchor="middle">2</text>
                                    <line x1="16" y1="20" x2="48" y2="20" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <text x="10" y="30" fontSize="6" fill="#3b82f6" textAnchor="middle">3</text>
                                    <line x1="16" y1="28" x2="48" y2="28" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <text x="10" y="38" fontSize="6" fill="#3b82f6" textAnchor="middle">4</text>
                                    <line x1="16" y1="36" x2="48" y2="36" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "todo-list":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="4" height="4" fill="none" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="16" y1="10" x2="48" y2="10" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <rect x="8" y="18" width="4" height="4" fill="#fed7aa" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="16" y1="20" x2="48" y2="20" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <rect x="8" y="28" width="4" height="4" fill="none" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="16" y1="30" x2="48" y2="30" stroke="#6b7280" strokeWidth="1"/>
                                    
                                    <rect x="8" y="38" width="4" height="4" fill="#fed7aa" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="16" y1="40" x2="48" y2="40" stroke="#6b7280" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "pros-cons":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="56" height="40" fill="none" stroke="#eab308" strokeWidth="1"/>
                                    <line x1="32" y1="4" x2="32" y2="44" stroke="#eab308" strokeWidth="2"/>
                                    <line x1="4" y1="16" x2="60" y2="16" stroke="#eab308" strokeWidth="1"/>
                                    <text x="18" y="12" fontSize="6" fill="#22c55e" textAnchor="middle">PROS</text>
                                    <text x="46" y="12" fontSize="6" fill="#ef4444" textAnchor="middle">CONS</text>
                                    <text x="18" y="22" fontSize="4" fill="#16a34a" textAnchor="middle">+</text>
                                    <text x="18" y="30" fontSize="4" fill="#16a34a" textAnchor="middle">+</text>
                                    <text x="18" y="38" fontSize="4" fill="#16a34a" textAnchor="middle">+</text>
                                    <text x="46" y="22" fontSize="4" fill="#dc2626" textAnchor="middle">-</text>
                                    <text x="46" y="30" fontSize="4" fill="#dc2626" textAnchor="middle">-</text>
                                    <text x="46" y="38" fontSize="4" fill="#dc2626" textAnchor="middle">-</text>
                                  </svg>
                                </div>
                              );
                            case "scrum-board":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="8" width="16" height="32" fill="#06b6d4" stroke="#0891b2" strokeWidth="1" rx="2"/>
                                    <rect x="24" y="8" width="16" height="32" fill="#22d3ee" stroke="#06b6d4" strokeWidth="1" rx="2"/>
                                    <rect x="44" y="8" width="16" height="32" fill="#67e8f9" stroke="#22d3ee" strokeWidth="1" rx="2"/>
                                    <text x="12" y="14" fontSize="4" fill="white" textAnchor="middle">TODO</text>
                                    <text x="32" y="14" fontSize="4" fill="white" textAnchor="middle">DOING</text>
                                    <text x="52" y="14" fontSize="4" fill="white" textAnchor="middle">DONE</text>
                                    <rect x="6" y="18" width="12" height="4" fill="white" rx="1"/>
                                    <rect x="6" y="24" width="12" height="4" fill="white" rx="1"/>
                                    <rect x="26" y="18" width="12" height="4" fill="white" rx="1"/>
                                    <rect x="46" y="18" width="12" height="4" fill="white" rx="1"/>
                                    <rect x="46" y="24" width="12" height="4" fill="white" rx="1"/>
                                    <rect x="46" y="30" width="12" height="4" fill="white" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "sticky-notes":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="12" height="12" fill="#fef08a" stroke="#eab308" strokeWidth="1" rx="1"/>
                                    <rect x="24" y="6" width="12" height="12" fill="#fde047" stroke="#facc15" strokeWidth="1" rx="1"/>
                                    <rect x="40" y="10" width="12" height="12" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1" rx="1"/>
                                    <rect x="12" y="24" width="12" height="12" fill="#fed7aa" stroke="#fb923c" strokeWidth="1" rx="1"/>
                                    <rect x="32" y="26" width="12" height="12" fill="#fecdd3" stroke="#f87171" strokeWidth="1" rx="1"/>
                                    <line x1="10" y1="12" x2="18" y2="12" stroke="#d97706" strokeWidth="0.5"/>
                                    <line x1="10" y1="14" x2="18" y2="14" stroke="#d97706" strokeWidth="0.5"/>
                                    <line x1="26" y1="10" x2="34" y2="10" stroke="#d97706" strokeWidth="0.5"/>
                                    <line x1="26" y1="12" x2="34" y2="12" stroke="#d97706" strokeWidth="0.5"/>
                                  </svg>
                                </div>
                              );
                            case "card-layout":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="24" height="18" fill="white" stroke="#64748b" strokeWidth="1" rx="3"/>
                                    <rect x="36" y="4" width="24" height="18" fill="white" stroke="#64748b" strokeWidth="1" rx="3"/>
                                    <rect x="4" y="26" width="24" height="18" fill="white" stroke="#64748b" strokeWidth="1" rx="3"/>
                                    <rect x="36" y="26" width="24" height="18" fill="white" stroke="#64748b" strokeWidth="1" rx="3"/>
                                    <circle cx="16" cy="10" r="2" fill="#94a3b8"/>
                                    <line x1="8" y1="16" x2="24" y2="16" stroke="#cbd5e1" strokeWidth="1"/>
                                    <line x1="8" y1="18" x2="20" y2="18" stroke="#e2e8f0" strokeWidth="1"/>
                                    <circle cx="48" cy="10" r="2" fill="#94a3b8"/>
                                    <line x1="40" y1="16" x2="56" y2="16" stroke="#cbd5e1" strokeWidth="1"/>
                                    <line x1="40" y1="18" x2="52" y2="18" stroke="#e2e8f0" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "wireframe":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="56" height="40" fill="none" stroke="#6b7280" strokeWidth="1"/>
                                    <rect x="8" y="8" width="48" height="8" fill="none" stroke="#9ca3af" strokeWidth="1"/>
                                    <rect x="8" y="20" width="20" height="20" fill="none" stroke="#9ca3af" strokeWidth="1"/>
                                    <rect x="32" y="20" width="24" height="8" fill="none" stroke="#9ca3af" strokeWidth="1"/>
                                    <rect x="32" y="32" width="24" height="8" fill="none" stroke="#9ca3af" strokeWidth="1"/>
                                    <line x1="8" y1="12" x2="56" y2="12" stroke="#d1d5db" strokeWidth="1"/>
                                    <line x1="32" y1="24" x2="56" y2="24" stroke="#d1d5db" strokeWidth="1"/>
                                    <line x1="32" y1="36" x2="56" y2="36" stroke="#d1d5db" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "mockup":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="4" width="48" height="40" fill="#dbeafe" stroke="#3b82f6" strokeWidth="2" rx="4"/>
                                    <rect x="12" y="8" width="40" height="6" fill="#3b82f6" rx="2"/>
                                    <circle cx="16" cy="20" r="3" fill="#60a5fa"/>
                                    <rect x="24" y="18" width="28" height="2" fill="#93c5fd" rx="1"/>
                                    <rect x="24" y="22" width="20" height="2" fill="#bfdbfe" rx="1"/>
                                    <rect x="12" y="30" width="40" height="2" fill="#93c5fd" rx="1"/>
                                    <rect x="12" y="34" width="32" height="2" fill="#bfdbfe" rx="1"/>
                                    <rect x="12" y="38" width="24" height="4" fill="#3b82f6" rx="2"/>
                                  </svg>
                                </div>
                              );
                            case "prototype":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="24" height="40" fill="#f3e8ff" stroke="#8b5cf6" strokeWidth="1" rx="3"/>
                                    <rect x="36" y="8" width="24" height="32" fill="#e9d5ff" stroke="#a78bfa" strokeWidth="1" rx="2"/>
                                    <rect x="8" y="8" width="16" height="3" fill="#8b5cf6" rx="1"/>
                                    <rect x="8" y="14" width="16" height="8" fill="#c4b5fd" rx="1"/>
                                    <rect x="8" y="26" width="16" height="6" fill="#ddd6fe" rx="1"/>
                                    <circle cx="48" cy="16" r="3" fill="#a78bfa"/>
                                    <rect x="40" y="22" width="16" height="2" fill="#c4b5fd" rx="1"/>
                                    <rect x="40" y="26" width="12" height="2" fill="#ddd6fe" rx="1"/>
                                    <line x1="28" y1="20" x2="36" y2="20" stroke="#8b5cf6" strokeWidth="2" markerEnd="url(#arrow)"/>
                                  </svg>
                                </div>
                              );
                            case "user-journey":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-pink-100 to-pink-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <circle cx="12" cy="24" r="4" fill="#ec4899"/>
                                    <rect x="20" y="20" width="8" height="8" fill="#f9a8d4" rx="2"/>
                                    <rect x="32" y="20" width="8" height="8" fill="#fbcfe8" rx="2"/>
                                    <circle cx="48" cy="24" r="4" fill="#fdf2f8" stroke="#ec4899" strokeWidth="2"/>
                                    <line x1="16" y1="24" x2="20" y2="24" stroke="#be185d" strokeWidth="2"/>
                                    <line x1="28" y1="24" x2="32" y2="24" stroke="#be185d" strokeWidth="2"/>
                                    <line x1="40" y1="24" x2="44" y2="24" stroke="#be185d" strokeWidth="2"/>
                                    <path d="M 12 16 Q 12 12 16 12 Q 20 12 20 16" fill="none" stroke="#ec4899" strokeWidth="1"/>
                                    <path d="M 24 16 Q 24 12 28 12 Q 32 12 32 16" fill="none" stroke="#ec4899" strokeWidth="1"/>
                                    <path d="M 36 16 Q 36 12 40 12 Q 44 12 44 16" fill="none" stroke="#ec4899" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "sitemap":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="24" y="4" width="16" height="8" fill="#22c55e" rx="2"/>
                                    <rect x="8" y="20" width="12" height="6" fill="#4ade80" rx="1"/>
                                    <rect x="26" y="20" width="12" height="6" fill="#4ade80" rx="1"/>
                                    <rect x="44" y="20" width="12" height="6" fill="#4ade80" rx="1"/>
                                    <rect x="4" y="32" width="8" height="6" fill="#86efac" rx="1"/>
                                    <rect x="16" y="32" width="8" height="6" fill="#86efac" rx="1"/>
                                    <rect x="28" y="32" width="8" height="6" fill="#86efac" rx="1"/>
                                    <rect x="40" y="32" width="8" height="6" fill="#86efac" rx="1"/>
                                    <rect x="52" y="32" width="8" height="6" fill="#86efac" rx="1"/>
                                    <line x1="32" y1="12" x2="14" y2="20" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="32" y2="20" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="32" y1="12" x2="50" y2="20" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="14" y1="26" x2="8" y2="32" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="14" y1="26" x2="20" y2="32" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="32" y1="26" x2="32" y2="32" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="50" y1="26" x2="44" y2="32" stroke="#16a34a" strokeWidth="1"/>
                                    <line x1="50" y1="26" x2="56" y2="32" stroke="#16a34a" strokeWidth="1"/>
                                  </svg>
                                </div>
                              );
                            case "infographic":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="4" width="48" height="40" fill="#fed7aa" stroke="#f97316" strokeWidth="1" rx="2"/>
                                    <circle cx="20" cy="14" r="4" fill="#ea580c"/>
                                    <rect x="28" y="10" width="24" height="3" fill="#fb923c" rx="1"/>
                                    <rect x="28" y="15" width="20" height="2" fill="#fdba74" rx="1"/>
                                    <rect x="12" y="22" width="8" height="12" fill="#f97316"/>
                                    <rect x="24" y="26" width="8" height="8" fill="#fb923c"/>
                                    <rect x="36" y="20" width="8" height="14" fill="#ea580c"/>
                                    <rect x="48" y="24" width="8" height="10" fill="#fdba74"/>
                                    <line x1="12" y1="36" x2="56" y2="36" stroke="#9a3412" strokeWidth="1"/>
                                    <rect x="12" y="38" width="44" height="2" fill="#fed7aa" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "poster":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="4" width="48" height="40" fill="#e0e7ff" stroke="#6366f1" strokeWidth="2" rx="2"/>
                                    <rect x="12" y="8" width="40" height="8" fill="#6366f1" rx="2"/>
                                    <circle cx="20" cy="24" r="6" fill="#a5b4fc"/>
                                    <rect x="30" y="20" width="20" height="3" fill="#8b5cf6" rx="1"/>
                                    <rect x="30" y="25" width="16" height="2" fill="#c7d2fe" rx="1"/>
                                    <rect x="30" y="29" width="18" height="2" fill="#c7d2fe" rx="1"/>
                                    <rect x="12" y="36" width="40" height="4" fill="#6366f1" rx="2"/>
                                  </svg>
                                </div>
                              );
                            case "brochure":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="8" width="18" height="32" fill="#cffafe" stroke="#06b6d4" strokeWidth="1"/>
                                    <rect x="22" y="8" width="18" height="32" fill="#a5f3fc" stroke="#22d3ee" strokeWidth="1"/>
                                    <rect x="40" y="8" width="18" height="32" fill="#67e8f9" stroke="#0891b2" strokeWidth="1"/>
                                    <line x1="22" y1="8" x2="22" y2="40" stroke="#0891b2" strokeWidth="2"/>
                                    <line x1="40" y1="8" x2="40" y2="40" stroke="#0891b2" strokeWidth="2"/>
                                    <rect x="6" y="12" width="14" height="2" fill="#0891b2" rx="1"/>
                                    <rect x="6" y="16" width="10" height="1" fill="#22d3ee" rx="1"/>
                                    <rect x="24" y="12" width="14" height="2" fill="#0891b2" rx="1"/>
                                    <rect x="24" y="16" width="10" height="1" fill="#22d3ee" rx="1"/>
                                    <rect x="42" y="12" width="14" height="2" fill="#0891b2" rx="1"/>
                                    <rect x="42" y="16" width="10" height="1" fill="#22d3ee" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "flyer":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-rose-100 to-rose-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="12" y="4" width="40" height="40" fill="#fecdd3" stroke="#e11d48" strokeWidth="2" rx="3"/>
                                    <rect x="16" y="8" width="32" height="6" fill="#e11d48" rx="2"/>
                                    <circle cx="24" cy="22" r="4" fill="#f43f5e"/>
                                    <rect x="32" y="18" width="16" height="2" fill="#fb7185" rx="1"/>
                                    <rect x="32" y="22" width="12" height="2" fill="#fda4af" rx="1"/>
                                    <rect x="32" y="26" width="14" height="2" fill="#fda4af" rx="1"/>
                                    <rect x="16" y="32" width="32" height="3" fill="#e11d48" rx="1"/>
                                    <rect x="16" y="38" width="32" height="2" fill="#f43f5e" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "swot-analysis":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="56" height="40" fill="none" stroke="#eab308" strokeWidth="2"/>
                                    <line x1="32" y1="4" x2="32" y2="44" stroke="#eab308" strokeWidth="2"/>
                                    <line x1="4" y1="24" x2="60" y2="24" stroke="#eab308" strokeWidth="2"/>
                                    <text x="18" y="16" fontSize="6" fill="#16a34a" textAnchor="middle" fontWeight="bold">S</text>
                                    <text x="46" y="16" fontSize="6" fill="#dc2626" textAnchor="middle" fontWeight="bold">W</text>
                                    <text x="18" y="36" fontSize="6" fill="#3b82f6" textAnchor="middle" fontWeight="bold">O</text>
                                    <text x="46" y="36" fontSize="6" fill="#7c2d12" textAnchor="middle" fontWeight="bold">T</text>
                                    <rect x="8" y="18" width="20" height="2" fill="#22c55e" rx="1"/>
                                    <rect x="36" y="18" width="20" height="2" fill="#ef4444" rx="1"/>
                                    <rect x="8" y="38" width="20" height="2" fill="#3b82f6" rx="1"/>
                                    <rect x="36" y="38" width="20" height="2" fill="#a16207" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "business-model":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="56" height="40" fill="none" stroke="#10b981" strokeWidth="1"/>
                                    <rect x="8" y="8" width="16" height="16" fill="#d1fae5" stroke="#059669" strokeWidth="1"/>
                                    <rect x="24" y="8" width="16" height="16" fill="#a7f3d0" stroke="#059669" strokeWidth="1"/>
                                    <rect x="40" y="8" width="16" height="16" fill="#6ee7b7" stroke="#059669" strokeWidth="1"/>
                                    <rect x="8" y="24" width="16" height="16" fill="#34d399" stroke="#059669" strokeWidth="1"/>
                                    <rect x="24" y="24" width="16" height="16" fill="#10b981" stroke="#047857" strokeWidth="1"/>
                                    <rect x="40" y="24" width="16" height="16" fill="#059669" stroke="#047857" strokeWidth="1"/>
                                    <text x="16" y="18" fontSize="4" fill="#065f46" textAnchor="middle">KP</text>
                                    <text x="32" y="18" fontSize="4" fill="#065f46" textAnchor="middle">KA</text>
                                    <text x="48" y="18" fontSize="4" fill="#065f46" textAnchor="middle">VP</text>
                                    <text x="16" y="34" fontSize="4" fill="#065f46" textAnchor="middle">CR</text>
                                    <text x="32" y="34" fontSize="4" fill="white" textAnchor="middle">CS</text>
                                    <text x="48" y="34" fontSize="4" fill="white" textAnchor="middle">CH</text>
                                  </svg>
                                </div>
                              );
                            case "value-chain":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="16" width="12" height="16" fill="#ddd6fe" stroke="#8b5cf6" strokeWidth="1" rx="2"/>
                                    <rect x="20" y="16" width="12" height="16" fill="#c4b5fd" stroke="#7c3aed" strokeWidth="1" rx="2"/>
                                    <rect x="36" y="16" width="12" height="16" fill="#a78bfa" stroke="#6d28d9" strokeWidth="1" rx="2"/>
                                    <rect x="52" y="16" width="8" height="16" fill="#8b5cf6" stroke="#6d28d9" strokeWidth="1" rx="2"/>
                                    <line x1="16" y1="24" x2="20" y2="24" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <line x1="32" y1="24" x2="36" y2="24" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <line x1="48" y1="24" x2="52" y2="24" stroke="#4c1d95" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <text x="10" y="26" fontSize="4" fill="#4c1d95" textAnchor="middle">IN</text>
                                    <text x="26" y="26" fontSize="4" fill="#4c1d95" textAnchor="middle">OP</text>
                                    <text x="42" y="26" fontSize="4" fill="#4c1d95" textAnchor="middle">MK</text>
                                    <text x="56" y="26" fontSize="4" fill="white" textAnchor="middle">S</text>
                                  </svg>
                                </div>
                              );
                            case "lean-canvas":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="56" height="40" fill="none" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="16" y1="4" x2="16" y2="44" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="32" y1="4" x2="32" y2="44" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="48" y1="4" x2="48" y2="44" stroke="#f97316" strokeWidth="1"/>
                                    <line x1="4" y1="24" x2="60" y2="24" stroke="#f97316" strokeWidth="1"/>
                                    <text x="10" y="16" fontSize="3" fill="#ea580c" textAnchor="middle">P</text>
                                    <text x="24" y="16" fontSize="3" fill="#ea580c" textAnchor="middle">S</text>
                                    <text x="40" y="16" fontSize="3" fill="#ea580c" textAnchor="middle">VP</text>
                                    <text x="54" y="16" fontSize="3" fill="#ea580c" textAnchor="middle">A</text>
                                    <text x="10" y="36" fontSize="3" fill="#ea580c" textAnchor="middle">C$</text>
                                    <text x="24" y="36" fontSize="3" fill="#ea580c" textAnchor="middle">KM</text>
                                    <text x="40" y="36" fontSize="3" fill="#ea580c" textAnchor="middle">KR</text>
                                    <text x="54" y="36" fontSize="3" fill="#ea580c" textAnchor="middle">R$</text>
                                  </svg>
                                </div>
                              );
                            case "lesson-plan":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="4" width="48" height="40" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1" rx="2"/>
                                    <rect x="12" y="8" width="40" height="4" fill="#3b82f6" rx="1"/>
                                    <circle cx="16" cy="18" r="2" fill="#60a5fa"/>
                                    <line x1="20" y1="18" x2="48" y2="18" stroke="#93c5fd" strokeWidth="1"/>
                                    <circle cx="16" cy="24" r="2" fill="#60a5fa"/>
                                    <line x1="20" y1="24" x2="48" y2="24" stroke="#93c5fd" strokeWidth="1"/>
                                    <circle cx="16" cy="30" r="2" fill="#60a5fa"/>
                                    <line x1="20" y1="30" x2="48" y2="30" stroke="#93c5fd" strokeWidth="1"/>
                                    <rect x="12" y="36" width="40" height="4" fill="#3b82f6" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "study-guide":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="4" width="48" height="40" fill="#dcfce7" stroke="#22c55e" strokeWidth="1" rx="2"/>
                                    <rect x="12" y="8" width="40" height="3" fill="#22c55e" rx="1"/>
                                    <rect x="12" y="14" width="32" height="2" fill="#4ade80" rx="1"/>
                                    <rect x="12" y="18" width="28" height="1" fill="#86efac" rx="1"/>
                                    <rect x="12" y="22" width="36" height="1" fill="#86efac" rx="1"/>
                                    <rect x="12" y="26" width="30" height="1" fill="#86efac" rx="1"/>
                                    <rect x="12" y="32" width="32" height="2" fill="#4ade80" rx="1"/>
                                    <rect x="12" y="36" width="24" height="1" fill="#86efac" rx="1"/>
                                    <rect x="12" y="40" width="28" height="1" fill="#86efac" rx="1"/>
                                  </svg>
                                </div>
                              );
                            case "flashcards":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="24" height="16" fill="#fef08a" stroke="#eab308" strokeWidth="1" rx="2"/>
                                    <rect x="32" y="24" width="24" height="16" fill="#fde047" stroke="#facc15" strokeWidth="1" rx="2"/>
                                    <rect x="16" y="16" width="24" height="16" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1" rx="2"/>
                                    <text x="20" y="18" fontSize="6" fill="#92400e" textAnchor="middle">Q</text>
                                    <text x="28" y="26" fontSize="6" fill="#92400e" textAnchor="middle">A</text>
                                    <text x="44" y="34" fontSize="6" fill="#92400e" textAnchor="middle">?</text>
                                  </svg>
                                </div>
                              );
                            case "quiz":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="4" width="48" height="40" fill="#e0e7ff" stroke="#6366f1" strokeWidth="1" rx="2"/>
                                    <text x="32" y="14" fontSize="8" fill="#4f46e5" textAnchor="middle">?</text>
                                    <circle cx="16" cy="22" r="2" fill="none" stroke="#8b5cf6" strokeWidth="1"/>
                                    <text x="20" y="24" fontSize="4" fill="#6366f1">A</text>
                                    <circle cx="16" cy="28" r="2" fill="none" stroke="#8b5cf6" strokeWidth="1"/>
                                    <text x="20" y="30" fontSize="4" fill="#6366f1">B</text>
                                    <circle cx="16" cy="34" r="2" fill="#8b5cf6" stroke="#8b5cf6" strokeWidth="1"/>
                                    <text x="20" y="36" fontSize="4" fill="#6366f1">C</text>
                                    <circle cx="16" cy="40" r="2" fill="none" stroke="#8b5cf6" strokeWidth="1"/>
                                    <text x="20" y="42" fontSize="4" fill="#6366f1">D</text>
                                  </svg>
                                </div>
                              );
                            case "architecture":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="32" width="48" height="12" fill="#e5e7eb" stroke="#6b7280" strokeWidth="1"/>
                                    <rect x="16" y="20" width="32" height="12" fill="#d1d5db" stroke="#6b7280" strokeWidth="1"/>
                                    <rect x="24" y="8" width="16" height="12" fill="#9ca3af" stroke="#6b7280" strokeWidth="1"/>
                                    <line x1="8" y1="32" x2="56" y2="32" stroke="#374151" strokeWidth="2"/>
                                    <line x1="16" y1="20" x2="48" y2="20" stroke="#374151" strokeWidth="1"/>
                                    <line x1="24" y1="8" x2="40" y2="8" stroke="#374151" strokeWidth="1"/>
                                    <text x="32" y="40" fontSize="4" fill="#374151" textAnchor="middle">DB</text>
                                    <text x="32" y="28" fontSize="4" fill="#374151" textAnchor="middle">API</text>
                                    <text x="32" y="16" fontSize="4" fill="#374151" textAnchor="middle">UI</text>
                                  </svg>
                                </div>
                              );
                            case "deployment":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="4" y="4" width="24" height="16" fill="#bbf7d0" stroke="#22c55e" strokeWidth="1" rx="2"/>
                                    <rect x="36" y="4" width="24" height="16" fill="#a7f3d0" stroke="#10b981" strokeWidth="1" rx="2"/>
                                    <rect x="4" y="28" width="24" height="16" fill="#6ee7b7" stroke="#059669" strokeWidth="1" rx="2"/>
                                    <rect x="36" y="28" width="24" height="16" fill="#34d399" stroke="#047857" strokeWidth="1" rx="2"/>
                                    <line x1="28" y1="12" x2="36" y2="12" stroke="#16a34a" strokeWidth="2"/>
                                    <line x1="28" y1="36" x2="36" y2="36" stroke="#16a34a" strokeWidth="2"/>
                                    <line x1="16" y1="20" x2="16" y2="28" stroke="#16a34a" strokeWidth="2"/>
                                    <line x1="48" y1="20" x2="48" y2="28" stroke="#16a34a" strokeWidth="2"/>
                                    <text x="16" y="14" fontSize="4" fill="#065f46" textAnchor="middle">WEB</text>
                                    <text x="48" y="14" fontSize="4" fill="#065f46" textAnchor="middle">API</text>
                                    <text x="16" y="38" fontSize="4" fill="#065f46" textAnchor="middle">CDN</text>
                                    <text x="48" y="38" fontSize="4" fill="#065f46" textAnchor="middle">DB</text>
                                  </svg>
                                </div>
                              );
                            case "sequence":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="8" height="32" fill="#dbeafe" stroke="#3b82f6" strokeWidth="1"/>
                                    <rect x="24" y="8" width="8" height="32" fill="#bfdbfe" stroke="#60a5fa" strokeWidth="1"/>
                                    <rect x="40" y="8" width="8" height="32" fill="#93c5fd" stroke="#93c5fd" strokeWidth="1"/>
                                    <line x1="12" y1="4" x2="12" y2="44" stroke="#1e40af" strokeWidth="2"/>
                                    <line x1="28" y1="4" x2="28" y2="44" stroke="#1e40af" strokeWidth="2"/>
                                    <line x1="44" y1="4" x2="44" y2="44" stroke="#1e40af" strokeWidth="2"/>
                                    <line x1="12" y1="16" x2="28" y2="16" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <line x1="28" y1="24" x2="44" y2="24" stroke="#ef4444" strokeWidth="2" markerEnd="url(#arrow)"/>
                                    <line x1="44" y1="32" x2="28" y2="32" stroke="#22c55e" strokeWidth="2" markerEnd="url(#arrow)"/>
                                  </svg>
                                </div>
                              );
                            case "class-diagram":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <rect x="8" y="8" width="20" height="16" fill="#f3e8ff" stroke="#8b5cf6" strokeWidth="1"/>
                                    <line x1="8" y1="14" x2="28" y2="14" stroke="#8b5cf6" strokeWidth="1"/>
                                    <line x1="8" y1="18" x2="28" y2="18" stroke="#8b5cf6" strokeWidth="1"/>
                                    <rect x="36" y="8" width="20" height="16" fill="#e9d5ff" stroke="#a78bfa" strokeWidth="1"/>
                                    <line x1="36" y1="14" x2="56" y2="14" stroke="#a78bfa" strokeWidth="1"/>
                                    <line x1="36" y1="18" x2="56" y2="18" stroke="#a78bfa" strokeWidth="1"/>
                                    <rect x="8" y="32" width="20" height="12" fill="#ddd6fe" stroke="#c4b5fd" strokeWidth="1"/>
                                    <line x1="8" y1="36" x2="28" y2="36" stroke="#c4b5fd" strokeWidth="1"/>
                                    <line x1="28" y1="16" x2="36" y2="16" stroke="#7c3aed" strokeWidth="2"/>
                                    <line x1="18" y1="24" x2="18" y2="32" stroke="#7c3aed" strokeWidth="2"/>
                                    <text x="18" y="12" fontSize="4" fill="#581c87" textAnchor="middle">Class</text>
                                    <text x="46" y="12" fontSize="4" fill="#581c87" textAnchor="middle">Class</text>
                                    <text x="18" y="34" fontSize="4" fill="#581c87" textAnchor="middle">Class</text>
                                  </svg>
                                </div>
                              );
                            case "use-case":
                              return (
                                <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
                                  <svg viewBox="0 0 64 48" className="w-full h-full">
                                    <ellipse cx="32" cy="16" rx="12" ry="6" fill="#a7f3d0" stroke="#14b8a6" strokeWidth="1"/>
                                    <ellipse cx="32" cy="32" rx="12" ry="6" fill="#6ee7b7" stroke="#0d9488" strokeWidth="1"/>
                                    <circle cx="12" cy="24" r="4" fill="#5eead4" stroke="#0f766e"/>
                                    <circle cx="52" cy="24" r="4" fill="#5eead4" stroke="#0f766e"/>
                                    <line x1="16" y1="24" x2="20" y2="16" stroke="#0f766e" strokeWidth="1"/>
                                    <line x1="16" y1="24" x2="20" y2="32" stroke="#0f766e" strokeWidth="1"/>
                                    <line x1="48" y1="24" x2="44" y2="16" stroke="#0f766e" strokeWidth="1"/>
                                    <line x1="48" y1="24" x2="44" y2="32" stroke="#0f766e" strokeWidth="1"/>
                                    <text x="32" y="18" fontSize="4" fill="#134e4a" textAnchor="middle">Use Case</text>
                                    <text x="32" y="34" fontSize="4" fill="#134e4a" textAnchor="middle">Use Case</text>
                                  </svg>
                                </div>
                              );
                            default:
                              return (
                                <div className="w-16 h-12 bg-gray-100 rounded mb-2 flex items-center justify-center border border-gray-200">
                                  <Layout className="w-6 h-6 text-gray-600" />
                                </div>
                              );
                          }
                        };
                        
                        return (
                          <div
                            key={vType.value}
                            className={`flex flex-col items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground/50'
                            }`}
                            onClick={() => toggleVisualType(vType.value)}
                            title={vType.preview}
                          >
                            {getPreviewSvg(vType.value)}
                            <div className="text-center">
                              <div className="text-xs font-medium truncate w-full">{vType.label}</div>
                              <div className="text-[10px] text-muted-foreground mt-0.5 truncate w-full">{vType.preview.replace(/^[^\s]+ /, '')}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {selectedVisualTypes.length > 1 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedVisualTypes.map(typeValue => {
                          const type = visualTypes.find(t => t.value === typeValue);
                          return (
                            <Badge key={typeValue} variant="secondary" className="text-xs">
                              {type?.label}
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-4 w-4 p-0 ml-1"
                                onClick={() => toggleVisualType(typeValue)}
                              >
                                <X className="w-2 h-2" />
                              </Button>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Intensity Level */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Change Intensity</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {intensityLevels.map((level) => (
                      <Button
                        key={level.value}
                        variant={intensity === level.value ? "default" : "outline"}
                        size="sm"
                        onClick={() => setIntensity(level.value)}
                        className="flex-col h-auto p-3 text-xs"
                      >
                        <span className="font-medium">{level.label}</span>
                        <span className="text-[10px] opacity-70 mt-1">{level.description}</span>
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Panel - Preview */}
              <div className="w-1/2 border-l border-border pl-6">
                {type === 'visual' && (
                  <div className="mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsVisualEditorMode(true)}
                      className="flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" />
                      Visual Editor
                    </Button>
                  </div>
                )}
                
                <Label className="text-sm font-medium mb-2 block">
                  {isGenerating ? "Preview (Generating...)" : "Current Content"}
                </Label>
                
                <div className="bg-muted/10 rounded-lg p-4 h-full overflow-y-auto">
                  {isGenerating && previewContent ? (
                    <div className="space-y-4">
                      {type === 'visual' ? (
                        <div 
                          className="w-full min-h-[200px] [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-h-[300px] [&>svg]:object-contain"
                          dangerouslySetInnerHTML={{ __html: sanitizeSvg(previewContent) }}
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">{previewContent}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <RotateCcw className="w-3 h-3 animate-spin" />
                        Generating new version...
                      </div>
                    </div>
                  ) : (
                    <>
                      {type === 'visual' ? (
                        <div 
                          className="w-full min-h-[200px] [&>svg]:w-full [&>svg]:h-auto [&>svg]:max-h-[300px] [&>svg]:object-contain"
                          dangerouslySetInnerHTML={{ __html: sanitizeSvg(currentContent) }}
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-wrap">
                          {currentContent.substring(0, 800)}
                          {currentContent.length > 800 ? "..." : ""}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-end gap-2 pt-4 border-t border-border">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={isGenerating}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit}
                disabled={isGenerating || (mode === 'describe' && !editPrompt.trim())}
              >
                {isGenerating ? (
                  <>
                    <RotateCcw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate New Version
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};